import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import AuthLayout from "../layouts/AuthLayout.jsx";
import apiClient from "../services/api.js";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    email: params.get("email") || "",
    code: "",
    new_password: "",
    confirm_password: ""
  });
  const [codeSent, setCodeSent] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const requestCode = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");
    setLoadingRequest(true);
    try {
      const { data } = await apiClient.post("/auth/password-reset/request", {
        email: form.email
      });
      setCodeSent(true);
      setStatus(
        data?.message ||
          "If an account exists, a reset code has been sent. If it does not arrive, check the email address or create an account."
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Could not send reset code");
    } finally {
      setLoadingRequest(false);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");
    if (form.new_password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    setLoadingReset(true);
    try {
      await apiClient.post("/auth/password-reset/confirm", {
        email: form.email,
        code: form.code,
        new_password: form.new_password
      });
      setStatus("Password reset successful. Redirecting to sign in...");
      window.setTimeout(() => navigate("/login", { replace: true }), 900);
    } catch (err) {
      setError(err?.response?.data?.message || "Password reset failed");
    } finally {
      setLoadingReset(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Receive an OTP in your email and set a new password for your member account."
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <span>Remember your password?</span>
          <Link className="font-semibold text-accent" to="/login">
            Sign in
          </Link>
        </div>
      }
    >
      <form className="space-y-4" onSubmit={requestCode}>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(129,178,154,0.4)]"
            placeholder="you@example.com"
            required
          />
        </div>
        <button
          type="submit"
          disabled={loadingRequest || !form.email}
          className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-600"
        >
          {loadingRequest ? "Sending..." : codeSent ? "Send code again" : "Send reset code"}
        </button>
      </form>

      <form className="mt-6 space-y-4 border-t border-black/10 pt-6" onSubmit={resetPassword}>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            OTP code
          </label>
          <input
            name="code"
            value={form.code}
            onChange={handleChange}
            className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-[rgba(129,178,154,0.4)]"
            placeholder="123456"
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            New password
          </label>
          <input
            type="password"
            name="new_password"
            value={form.new_password}
            onChange={handleChange}
            className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(129,178,154,0.4)]"
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            Confirm password
          </label>
          <input
            type="password"
            name="confirm_password"
            value={form.confirm_password}
            onChange={handleChange}
            className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(129,178,154,0.4)]"
            required
          />
        </div>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        {status && <p className="text-sm text-emerald-600">{status}</p>}
        <button
          type="submit"
          disabled={loadingReset}
          className="w-full rounded-2xl bg-accent px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-accent/90"
        >
          {loadingReset ? "Resetting..." : "Reset password"}
        </button>
      </form>
    </AuthLayout>
  );
}
